import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

// Constants
import { ROUTE_PATHS, ROUTE_NAMES } from '../../constants/routes';

// Types
import { INotificationCount, IMe } from '../../store/MeStore';
import { INetwork } from '../../store/NetworkStore';

// Assets
const logoThumbWhiteSVG = require('../../assets/logo/logo-thumb-white.svg');

// Components
import { FancyAvatar } from '../../components/Avatar';
import Badge from '../../components/Badge';
import Icon from '../../components/Icon';
import Link from '../../components/Link';
import { ListItemIcon, ListItemText } from '../../components/List';
import Menu, { MenuItem } from '../../components/Menu';
import Tooltip from '../../components/Tooltip';

// Style
import './AppBar.css';

// Component
export interface IProps {
  currentRouteName: string;
  user: IMe;
  role: string | null;
  network?: INetwork | null;
  notificationCount: INotificationCount;
  onNetworkClick: React.EventHandler<
    React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  >;
  onAccountClick: () => void;
  onLogOut: () => void;
  isMobile: boolean;
  isGoNative: boolean;
}

class AppHeader extends React.Component<IProps> {
  @observable
  accountDropdownState: { open: boolean; anchorEl: HTMLElement | undefined } = {
    open: false,
    anchorEl: undefined
  };

  handleNetworkDropdownRef = (ref: HTMLButtonElement | null) => {
    this.accountDropdownState.anchorEl = ref ? ref : undefined;
  };

  handleAccountDropdownClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    this.accountDropdownState.open = true;
  };

  handleAccountDropdownClose = () => {
    this.accountDropdownState.open = false;
  };

  handleAccountDropdownItemClick = (cb: () => void) => () => {
    this.accountDropdownState.open = false;
    cb();
  };

  renderBody() {
    if (this.props.network) {
      const networkId = this.props.network.networkId;
      const routeName = this.props.currentRouteName.split('.')[2];
      return (
        <ul className="App__AppBar__body">
          <li className="App__AppBar__item">
            <Tooltip title="Home" placement="right">
              <span>
                <Link
                  className={`App__AppBar__item__link ${
                    routeName === ROUTE_NAMES.APP_NETWORK_HOME
                      ? 'App__AppBar__item__link--active'
                      : ''
                  }`}
                  routeName={ROUTE_PATHS.APP_NETWORK_HOME}
                  routeParams={{ networkId }}
                >
                  <Icon>home</Icon>
                </Link>
              </span>
            </Tooltip>
          </li>
          <li className="App__AppBar__item">
            <Tooltip title="People" placement="right">
              <span>
                <Link
                  className={`App__AppBar__item__link ${
                    routeName === ROUTE_NAMES.APP_NETWORK_PEOPLE
                      ? 'App__AppBar__item__link--active'
                      : ''
                  }`}
                  routeName={ROUTE_PATHS.APP_NETWORK_PEOPLE}
                  routeParams={{ networkId }}
                >
                  <Icon>people</Icon>
                </Link>
              </span>
            </Tooltip>
          </li>
          <li className="App__AppBar__item">
            <Tooltip title="Questions" placement="right">
              <span>
                <Link
                  className={`App__AppBar__item__link ${
                    routeName === ROUTE_NAMES.APP_NETWORK_QUESTIONS
                      ? 'App__AppBar__item__link--active'
                      : ''
                  }`}
                  routeName={ROUTE_PATHS.APP_NETWORK_QUESTIONS}
                  routeParams={{ networkId }}
                >
                  <Icon>chat</Icon>
                </Link>
              </span>
            </Tooltip>
          </li>
          {this.props.role === 'ADMIN' ? (
            <li className="App__AppBar__item">
              <Tooltip title="Administration" placement="right">
                <span>
                  <Link
                    className={`App__AppBar__item__link ${
                      routeName === ROUTE_NAMES.APP_NETWORK_ADMINISTRATION
                        ? 'App__AppBar__item__link--active'
                        : ''
                    }`}
                    routeName={ROUTE_PATHS.APP_NETWORK_ADMINISTRATION}
                    routeParams={{ networkId }}
                  >
                    <Icon>settings</Icon>
                  </Link>
                </span>
              </Tooltip>
            </li>
          ) : null}
          <span style={{ flexGrow: 1 }} />
          <li className="App__AppBar__item">
            <Tooltip
              title={this.props.network.name}
              placement="right"
              disableTriggerFocus={true}
            >
              <button
                className="App__AppBar__item__link"
                onClick={this.props.onNetworkClick}
              >
                <FancyAvatar
                  text={this.props.network.name}
                  src={this.props.network.photoUrl}
                  rounded={false}
                  className="App__AppBar__avatar"
                />
              </button>
            </Tooltip>
          </li>
        </ul>
      );
    }

    return <span style={{ flexGrow: 1 }} />;
  }

  render() {
    const routeName = this.props.currentRouteName.split('.')[2];
    return (
      <div className="App__AppBar">
        <Link routeName={ROUTE_PATHS.APP} className="App__AppBar__header">
          <img
            style={{ display: 'block' }}
            src={logoThumbWhiteSVG}
            height={48}
            width={48}
          />
        </Link>
        {this.renderBody()}
        <ul className="App__AppBar__footer">
          {this.props.network ? (
            <li className="App__AppBar__item">
              <Tooltip
                title="Notifications"
                placement="right"
                disableTriggerFocus={true}
              >
                <Link
                  routeName={ROUTE_PATHS.APP_NETWORK_NOTIFICATIONS}
                  routeParams={{ networkId: this.props.network.networkId }}
                  className={`App__AppBar__item__link ${
                    routeName === ROUTE_NAMES.APP_NETWORK_NOTIFICATIONS
                      ? 'App__AppBar__item__link--active'
                      : ''
                  }`}
                >
                  {this.props.notificationCount.notificationCount > 0 ? (
                    <Badge badgeContent={this.props.notificationCount.notificationCount} color="secondary">
                      <Icon>notifications</Icon>
                    </Badge>
                  ) : (
                    <Icon>notifications</Icon>
                  )}
                </Link>
              </Tooltip>
            </li>
          ) : null}
          <li className="App__AppBar__item">
            <button
              ref={this.handleNetworkDropdownRef}
              className="App__AppBar__item__link"
              onClick={this.handleAccountDropdownClick}
            >
              <Icon>account_circle</Icon>
              {/*<FancyAvatar
                text={`${this.props.user.firstName} ${
                  this.props.user.lastName
                }`}
                src={this.props.user.photoUrl}
              />*/}
            </button>
            <Menu
              anchorEl={this.accountDropdownState.anchorEl}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 0, horizontal: -24 }}
              open={this.accountDropdownState.open}
              onClose={this.handleAccountDropdownClose}
            >
              <MenuItem
                onClick={this.handleAccountDropdownItemClick(
                  this.props.onAccountClick
                )}
              >
                <ListItemIcon>
                  <Icon>account_circle</Icon>
                </ListItemIcon>
                <ListItemText inset={true} primary="Account" />
              </MenuItem>
              <MenuItem
                onClick={this.handleAccountDropdownItemClick(
                  this.props.onLogOut
                )}
              >
                <ListItemIcon>
                  <Icon>exit_to_app</Icon>
                </ListItemIcon>
                <ListItemText inset={true} primary="Log out" />
              </MenuItem>
            </Menu>
          </li>
        </ul>
      </div>
    );
  }
}

export default observer(AppHeader);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/App/AppBar.tsx